import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithBackground.js";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import { FiShield, FiZap, FiUsers, FiSmile, FiBarChart2, FiGlobe, FiMonitor, FiLock, FiAward, FiBookOpen } from "react-icons/fi";

const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24`;
const ContentWithPaddingXl = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24 px-4 sm:px-6 lg:px-8`;
const ContentWithVerticalPadding = tw.div`py-20 lg:py-24`;
const Content2Xl = tw.div`max-w-screen-2xl mx-auto`;

const Heading = tw.h1`font-bold text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-gray-900 leading-tight`;
const HeadingRow = tw.div`flex flex-col items-center text-center`;
const Text = tw.div`mt-8 text-base text-gray-600 max-w-3xl mx-auto`;

const ValueGrid = tw.div`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12`;
const ValueCard = tw.div`bg-white rounded-lg shadow-lg p-8`;
const ValueIcon = tw.div`text-primary-500 text-4xl mb-4`;
const ValueTitle = tw.h3`text-xl font-semibold text-gray-900 mb-2`;
const ValueDescription = tw.p`text-gray-600`;

const EnvironmentSection = tw.div`mt-16`;
const EnvironmentGrid = tw.div`grid grid-cols-1 md:grid-cols-2 gap-8 mt-8`;
const EnvironmentCard = tw.div`bg-white rounded-lg shadow-lg overflow-hidden`;
const EnvironmentImage = tw.img`w-full h-64 object-cover`;
const EnvironmentContent = tw.div`p-8`;
const EnvironmentTitle = tw.h3`text-xl font-semibold text-gray-900 mb-2`;
const EnvironmentDescription = tw.p`text-gray-600`;

const PerksSection = tw.div`mt-16`;
const PerksGrid = tw.div`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-8`;
const PerkCard = tw.div`bg-white rounded-lg shadow-lg p-8 text-center`;
const PerkIcon = tw.div`text-primary-500 text-4xl mb-4`;
const PerkTitle = tw.h3`text-xl font-semibold text-gray-900 mb-2`;
const PerkDescription = tw.p`text-gray-600`;

export default () => {
  const values = [
    {
      icon: <FiShield />,
      title: "Sécurité",
      description: "Nous plaçons la sécurité des visiteurs et des entreprises au cœur de notre mission."
    },
    {
      icon: <FiZap />,
      title: "Innovation",
      description: "Nous innovons sans cesse pour offrir une expérience d'accueil digitale, rapide et fiable."
    },
    {
      icon: <FiUsers />,
      title: "Confiance",
      description: "Nous bâtissons des relations de confiance avec nos clients grâce à la transparence et la traçabilité."
    },
    {
      icon: <FiSmile />,
      title: "Expérience Utilisateur",
      description: "Nous simplifions l'accueil pour les visiteurs et les équipes, pour un check-in fluide et agréable."
    },
    {
      icon: <FiBarChart2 />,
      title: "Traçabilité",
      description: "Nous garantissons un suivi précis des accès et des flux visiteurs pour une gestion optimale."
    },
    {
      icon: <FiGlobe />,
      title: "Impact",
      description: "Nous contribuons à rendre les organisations plus sûres et plus accueillantes partout en Afrique."
    }
  ];

  const environment = [
    {
      title: "Sécurité & Conformité",
      description: "Un environnement où la sécurité, la confidentialité et la conformité RGPD sont des priorités absolues.",
      image: "https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
    },
    {
      title: "Esprit d'Innovation",
      description: "Une équipe passionnée par la digitalisation et l'amélioration continue de l'expérience visiteur.",
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
    }
  ];

  const perks = [
    {
      icon: <FiMonitor />,
      title: "Technologies Modernes",
      description: "Travaillez avec les meilleurs outils pour innover dans la gestion des visiteurs."
    },
    {
      icon: <FiLock />,
      title: "Formation Sécurité",
      description: "Formations régulières sur la sécurité, la confidentialité et la conformité."
    },
    {
      icon: <FiAward />,
      title: "Célébration de l'Innovation",
      description: "Nous célébrons chaque nouvelle étape vers un accueil plus sûr et plus digital."
    },
    {
      icon: <FiBookOpen />,
      title: "Croissance Personnelle",
      description: "Un environnement qui encourage l'apprentissage et l'évolution professionnelle."
    }
  ];

  return (
    <AnimationRevealPage>
      <Header />
      <Container>
        <ContentWithPaddingXl>
          <HeadingRow>
            <Heading>Notre Culture</Heading>
          </HeadingRow>
          <Text>
            <p className="text-center mb-8">
              Chez Tanga, nous sommes animés par la volonté de rendre chaque organisation plus sûre, plus efficace et plus accueillante grâce à la digitalisation de la gestion des visiteurs. Notre culture repose sur la sécurité, l'innovation et l'expérience utilisateur.
            </p>
          </Text>

          <ValueGrid>
            {values.map((value, index) => (
              <ValueCard key={index}>
                <ValueIcon>{value.icon}</ValueIcon>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueDescription>{value.description}</ValueDescription>
              </ValueCard>
            ))}
          </ValueGrid>

          <EnvironmentSection>
            <HeadingRow>
              <Heading>Notre Environnement</Heading>
            </HeadingRow>
            <Text>
              <p className="text-center mb-8">
                Un cadre de travail qui inspire et motive.
              </p>
            </Text>

            <EnvironmentGrid>
              {environment.map((item, index) => (
                <EnvironmentCard key={index}>
                  <EnvironmentImage src={item.image} alt={item.title} />
                  <EnvironmentContent>
                    <EnvironmentTitle>{item.title}</EnvironmentTitle>
                    <EnvironmentDescription>{item.description}</EnvironmentDescription>
                  </EnvironmentContent>
                </EnvironmentCard>
              ))}
            </EnvironmentGrid>
          </EnvironmentSection>

          <PerksSection>
            <HeadingRow>
              <Heading>Nos Avantages</Heading>
            </HeadingRow>
            <Text>
              <p className="text-center mb-8">
                Des avantages conçus pour votre bien-être et votre développement.
              </p>
            </Text>

            <PerksGrid>
              {perks.map((perk, index) => (
                <PerkCard key={index}>
                  <PerkIcon>{perk.icon}</PerkIcon>
                  <PerkTitle>{perk.title}</PerkTitle>
                  <PerkDescription>{perk.description}</PerkDescription>
                </PerkCard>
              ))}
            </PerksGrid>
          </PerksSection>
        </ContentWithPaddingXl>
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
}; 